import 'react-datepicker/dist/react-datepicker.css'
import './MonthRangePicker.css'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { format } from 'date-fns'
import { ComponentPropsWithoutRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import ReactDatePicker from 'react-datepicker'

import { cn } from '@sparkai/utils'

import { computeMonthRange, getDefaultMonthRange } from '../utils'
import { MonthRangePickerHeader } from './MonthRangePickerHeader'

export interface MonthRangePickerProps {
  readonly monthsRange: number
  readonly onChange: (value: { from: Date; to: Date }) => void
  readonly maxDate?: Date | null
  readonly className?: string
  readonly referenceDate?: Date
}

export function MonthRangePicker({
  monthsRange,
  onChange,
  maxDate,
  className,
  referenceDate = new Date(),
}: Readonly<MonthRangePickerProps>) {
  const [selectedMonth, setSelectedMonth] = useState<Date | null>(null)
  const [isDefaultSelected, setIsDefaultSelected] = useState(true)
  const pickerRef = useRef<ReactDatePicker>(null)

  useEffect(() => {
    onChange(getDefaultMonthRange(monthsRange, referenceDate))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleMonthChange = (date: Date) => {
    setSelectedMonth(date)
    setIsDefaultSelected(false)
    onChange(computeMonthRange(date, monthsRange, referenceDate))
  }

  const handleClickDefault = () => {
    setSelectedMonth(null)
    setIsDefaultSelected(true)
    onChange(getDefaultMonthRange(monthsRange, referenceDate))
    pickerRef?.current?.setOpen(false)
  }

  const displayedMonth = selectedMonth ?? referenceDate
  const currentRange = selectedMonth
    ? computeMonthRange(selectedMonth, monthsRange, referenceDate)
    : getDefaultMonthRange(monthsRange, referenceDate)
  const rangeLabel = `${format(currentRange.from, "d MMM ''yy")} - ${format(currentRange.to, "d MMM ''yy")}`

  return (
    <div className='date-month-range-picker-root'>
      <ReactDatePicker
        ref={pickerRef as React.RefObject<ReactDatePicker>}
        selected={displayedMonth}
        showPopperArrow={false}
        calendarClassName='shadow-xl ring-1 ring-black/5 p-6 border-0! rounded-md'
        customInput={<CustomInputSelect rangeLabel={rangeLabel} className={className} />}
        renderCustomHeader={(props) => (
          <MonthRangePickerHeader
            onClickDefault={handleClickDefault}
            isDefaultSelected={isDefaultSelected}
            monthsRange={monthsRange}
            referenceDate={referenceDate}
            {...props}
          />
        )}
        onChange={handleMonthChange}
        disabledKeyboardNavigation
        showMonthYearPicker
        maxDate={maxDate}
      />
    </div>
  )
}

type CustomInputSelectProps = ComponentPropsWithoutRef<'button'> & {
  rangeLabel?: string
}

const CustomInputSelect = forwardRef<HTMLButtonElement, CustomInputSelectProps>(function CustomInputSelect(
  { rangeLabel, className, ...props },
  ref
) {
  const innerRef = useRef<HTMLButtonElement>(null)
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => innerRef.current!)

  return (
    <button
      ref={innerRef}
      type='button'
      className={cn(
        'mt-1 flex w-full min-w-[180px] cursor-pointer items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 font-inter text-sm text-gray-900 shadow-xs',
        className
      )}
      aria-label='open calendar modal'
      {...props}
    >
      <span>{rangeLabel}</span>
      <ChevronUpDownIcon className='h-5 w-5 font-bold text-gray-400' aria-hidden='true' />
    </button>
  )
})
